import { decodeNostrEntity, type DecodedNostrEntity } from "./nip19"

export type ContentSegment =
  | { type: "text"; text: string }
  | { type: "link"; url: string }
  | { type: "image"; url: string; alt?: string }
  | { type: "video"; url: string }
  | { type: "audio"; url: string }
  | { type: "nostr"; raw: string; entity: DecodedNostrEntity }
  | { type: "hashtag"; tag: string }

const TOKEN_PATTERN =
  /(?:https?:\/\/[^\s<>"'`\]]+|nostr:(?:npub|nprofile|note|nevent|naddr)1[0-9a-z]+|#[\p{L}\p{N}_]{1,64})/giu

const IMAGE_PATTERN = /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?.*)?$/i
const VIDEO_PATTERN = /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i
const AUDIO_PATTERN = /\.(mp3|ogg|oga|wav|m4a|flac|aac)(\?.*)?$/i

/** Removes trailing punctuation that is sentence structure rather than a URL. */
function trimUrl(value: string): string {
  let url = value
  while (/[.,;:!?)\]]$/.test(url)) {
    const last = url.slice(-1)
    if (last === ")" && (url.match(/\(/g)?.length ?? 0) >= (url.match(/\)/g)?.length ?? 0)) break
    url = url.slice(0, -1)
  }
  return url
}

export function classifyUrl(url: string): ContentSegment {
  if (IMAGE_PATTERN.test(url)) return { type: "image", url }
  if (VIDEO_PATTERN.test(url)) return { type: "video", url }
  if (AUDIO_PATTERN.test(url)) return { type: "audio", url }
  return { type: "link", url }
}

function pushText(segments: ContentSegment[], text: string): void {
  if (!text) return
  const previous = segments[segments.length - 1]
  if (previous?.type === "text") {
    previous.text += text
    return
  }
  segments.push({ type: "text", text })
}

/**
 * Splits note content into renderable segments: text, links, media, NIP-21
 * entities and hashtags. Pure function, used by `NoteContent`.
 */
export function parseContent(content: string): ContentSegment[] {
  const segments: ContentSegment[] = []
  if (!content) return segments

  let lastIndex = 0
  for (const match of content.matchAll(TOKEN_PATTERN)) {
    const raw = match[0]
    const index = match.index ?? 0
    if (index > lastIndex) pushText(segments, content.slice(lastIndex, index))
    lastIndex = index + raw.length

    if (raw.startsWith("#")) {
      segments.push({ type: "hashtag", tag: raw.slice(1).toLowerCase() })
      continue
    }

    if (raw.startsWith("nostr:")) {
      const entity = decodeNostrEntity(raw)
      if (entity) {
        segments.push({ type: "nostr", raw, entity })
      } else {
        pushText(segments, raw)
      }
      continue
    }

    const url = trimUrl(raw)
    if (url.length < raw.length) lastIndex -= raw.length - url.length
    segments.push(classifyUrl(url))
  }

  if (lastIndex < content.length) pushText(segments, content.slice(lastIndex))
  return segments
}

/** Collects the media URLs of a note (content plus NIP-92 `imeta` alt text). */
export function extractMedia(
  content: string,
  tags: string[][] = [],
): Array<{ url: string; alt?: string; type: "image" | "video" | "audio" }> {
  const alts = new Map<string, string>()
  for (const tag of tags) {
    if (tag[0] !== "imeta") continue
    const urlEntry = tag.find((entry) => entry.startsWith("url "))
    const altEntry = tag.find((entry) => entry.startsWith("alt "))
    if (urlEntry) {
      const alt = altEntry?.slice(4).trim()
      if (alt) alts.set(urlEntry.slice(4).trim(), alt)
    }
  }

  const media: Array<{ url: string; alt?: string; type: "image" | "video" | "audio" }> = []
  const seen = new Set<string>()
  for (const segment of parseContent(content)) {
    if (segment.type !== "image" && segment.type !== "video" && segment.type !== "audio") continue
    if (seen.has(segment.url)) continue
    seen.add(segment.url)
    media.push({ url: segment.url, alt: alts.get(segment.url), type: segment.type })
  }
  return media
}

/** Pubkeys referenced inline (`nostr:npub…`) that are not already tagged. */
export function extractMentions(content: string): string[] {
  const mentions: string[] = []
  for (const segment of parseContent(content)) {
    if (segment.type !== "nostr") continue
    const { entity } = segment
    if (entity.type === "npub" || entity.type === "nprofile") {
      if (!mentions.includes(entity.pubkey)) mentions.push(entity.pubkey)
    }
  }
  return mentions
}

/** Event ids referenced inline (`nostr:note…` / `nostr:nevent…`). */
export function extractEventReferences(content: string): string[] {
  const ids: string[] = []
  for (const segment of parseContent(content)) {
    if (segment.type !== "nostr") continue
    const { entity } = segment
    if (entity.type === "note" || entity.type === "nevent") {
      if (!ids.includes(entity.id)) ids.push(entity.id)
    }
  }
  return ids
}

/** Hashtags from content and `t` tags, lowercased and de-duplicated. */
export function extractHashtags(content: string, tags: string[][] = []): string[] {
  const hashtags: string[] = []
  for (const tag of tags) {
    if (tag[0] !== "t" || !tag[1]) continue
    const value = tag[1].toLowerCase().replace(/^#/, "")
    if (value && !hashtags.includes(value)) hashtags.push(value)
  }
  for (const segment of parseContent(content)) {
    if (segment.type === "hashtag" && !hashtags.includes(segment.tag)) hashtags.push(segment.tag)
  }
  return hashtags
}

export function extractLinks(content: string): string[] {
  const links: string[] = []
  for (const segment of parseContent(content)) {
    if (segment.type === "link" && !links.includes(segment.url)) links.push(segment.url)
  }
  return links
}

/** Plain text preview used in relay/notification lists. */
export function noteExcerpt(content: string, max = 180): string {
  const text = content
    .replace(/nostr:(npub|nprofile|note|nevent|naddr)1[0-9a-z]+/gi, "")
    .replace(/\s+/g, " ")
    .trim()
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}
